import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/config';
import { Briefcase, Lock, User, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const AcceptInvite = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [invitation, setInvitation] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchInvitation = async () => {
            if (!token) {
                setError('Geçersiz davet bağlantısı.');
                setLoading(false);
                return;
            }

            try {
                const { data, error: fetchError } = await supabase
                    .from('invitations')
                    .select('*')
                    .eq('token', token);

                if (fetchError) throw fetchError;

                if (!data || data.length === 0) {
                    setError('Davetiye bulunamadı veya süresi dolmuş.');
                } else {
                    const row = data[0];
                    setInvitation({
                        id: row.id,
                        email: row.email,
                        firstName: row.first_name,
                        lastName: row.last_name,
                        role: row.role,
                        tenantId: row.tenant_id,
                        token: row.token,
                    });
                }
            } catch (err) {
                console.error(err);
                setError('Davetiye kontrol edilirken bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };

        fetchInvitation();
    }, [token]);

    const handleAccept = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Şifre en az 6 karakter olmalıdır.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Şifreler eşleşmiyor.');
            return;
        }

        setSubmitting(true);
        try {
            // 1. Create User in Auth
            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                email: invitation.email,
                password,
            });
            if (signUpError) throw signUpError;
            const uid = signUpData.user.id;

            // 2. Create User Doc in DB
            const { error: insertError } = await supabase.from('users').insert({
                id: uid,
                email: invitation.email,
                first_name: invitation.firstName,
                last_name: invitation.lastName,
                role: invitation.role,
                tenant_id: invitation.tenantId,
                status: 'active',
                created_at: new Date().toISOString(),
            });
            if (insertError) throw insertError;

            // 3. Delete the invitation
            const { error: deleteError } = await supabase.from('invitations').delete().eq('id', invitation.id);
            if (deleteError) throw deleteError;

            setIsSuccess(true);
            setTimeout(() => {
                navigate('/app');
            }, 3000);
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/email-already-in-use') {
                setError('Bu e-posta adresi zaten kullanımda.');
            } else {
                setError('Hesap oluşturulurken bir hata oluştu.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)' }}>
                <div style={{ textAlign: 'center' }}>
                    <Loader2 className="animate-spin" size={40} style={{ color: 'var(--primary)', margin: '0 auto 1rem' }} />
                    <p style={{ color: 'var(--text-muted)' }}>Davetiye kontrol ediliyor...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', padding: '20px' }}>
                <div className="card" style={{ maxWidth: '400px', textAlign: 'center' }}>
                    <AlertCircle size={48} style={{ color: '#ef4444', margin: '0 auto 1rem' }} />
                    <h2 style={{ marginBottom: '1rem' }}>Hata!</h2>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>{error}</p>
                    <button onClick={() => navigate('/login')} style={{ width: '100%' }}>Giriş Ekranına Dön</button>
                </div>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', padding: '20px' }}>
                <div className="card" style={{ maxWidth: '400px', textAlign: 'center' }}>
                    <CheckCircle size={48} style={{ color: '#10b981', margin: '0 auto 1rem' }} />
                    <h2 style={{ marginBottom: '1rem' }}>Hoş Geldiniz!</h2>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Hesabınız başarıyla oluşturuldu. Yönlendiriliyorsunuz...</p>
                    <div className="loading-bar" style={{ height: '4px', background: 'var(--bg-main)', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#10b981', width: '100%', transition: 'width 3s linear' }}></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', padding: '20px' }}>
            <div className="card shadow-2xl" style={{ width: '100%', maxWidth: '440px', padding: '2.5rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        background: 'var(--primary)',
                        color: 'white',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1.25rem',
                        boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.3)'
                    }}>
                        <Briefcase size={24} />
                    </div>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>Daveti Kabul Et</h1>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{invitation.firstName} {invitation.lastName}</span> olarak sisteme dahil oluyorsunuz. Lütfen şifrenizi belirleyin.
                    </p>
                </div>

                <form onSubmit={handleAccept}>
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>E-posta</label>
                        <div style={{ position: 'relative' }}>
                            <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                disabled
                                value={invitation.email}
                                style={{ paddingLeft: '40px', background: 'rgba(var(--primary-rgb), 0.02)', cursor: 'not-allowed' }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Şifre Oluştur</label>
                        <div style={{ position: 'relative' }}>
                            <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="password"
                                required
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={{ paddingLeft: '40px' }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '2rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Şifreyi Onayla</label>
                        <div style={{ position: 'relative' }}>
                            <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="password"
                                required
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                style={{ paddingLeft: '40px' }}
                            />
                        </div>
                    </div>

                    {error && (
                        <div style={{
                            background: '#fef2f2',
                            color: '#ef4444',
                            padding: '10px',
                            borderRadius: '8px',
                            fontSize: '0.8125rem',
                            marginBottom: '1.5rem',
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            border: '1px solid #fee2e2'
                        }}>
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        style={{ width: '100%', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                        {submitting ? <Loader2 className="animate-spin" size={18} /> : 'Hesabımı Oluştur'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AcceptInvite;
